/**
 * Grouping and search helpers for metric pickers behind the metric-categories
 * feature. Pure functions shared by the block-builder context menu and the
 * tablet AddMetricsModal so ordering/filtering stay identical in both.
 */

export interface MetricCategoryRef {
  id: string;
  name: string;
}

/** Minimal shape the helpers need — satisfied by the allMetrics tRPC output. */
export interface CategorizedMetric {
  id: string;
  name: string;
  sortOrder: number;
  categoryId?: string | null;
}

export interface MetricGroup<T extends CategorizedMetric> {
  /** null marks the uncategorized group, which always renders first */
  category: MetricCategoryRef | null;
  metrics: T[];
}

/**
 * Groups metrics by category: uncategorized metrics (or metrics whose
 * categoryId no longer resolves) in a flat group at the top, then groups
 * sorted alphabetically by category name, metrics sorted by sortOrder within
 * each group.
 */
export function groupMetricsByCategory<T extends CategorizedMetric>(
  metrics: T[],
  categories: MetricCategoryRef[],
): MetricGroup<T>[] {
  const categoriesById = new Map(categories.map((c) => [c.id, c] as const));
  const byCategory = new Map<
    string,
    { category: MetricCategoryRef; metrics: T[] }
  >();
  const uncategorized: T[] = [];

  for (const metric of metrics) {
    const category = metric.categoryId
      ? categoriesById.get(metric.categoryId)
      : undefined;
    if (category) {
      const group = byCategory.get(category.id) ?? { category, metrics: [] };
      group.metrics.push(metric);
      byCategory.set(category.id, group);
    } else {
      uncategorized.push(metric);
    }
  }

  const categorized = [...byCategory.values()].map((group) => ({
    category: group.category,
    metrics: [...group.metrics].sort((a, b) => a.sortOrder - b.sortOrder),
  }));
  categorized.sort((a, b) => a.category.name.localeCompare(b.category.name));

  const groups: MetricGroup<T>[] = [];

  if (uncategorized.length > 0) {
    groups.push({
      category: null,
      metrics: [...uncategorized].sort((a, b) => a.sortOrder - b.sortOrder),
    });
  }

  groups.push(...categorized);

  return groups;
}

/**
 * Filters metrics by a search query matching the metric name OR its category
 * name (case-insensitive). A blank query returns the input unchanged.
 */
export function searchMetrics<T extends CategorizedMetric>(
  metrics: T[],
  categories: MetricCategoryRef[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return metrics;
  }

  const matchingCategoryIds = new Set(
    categories.filter((c) => c.name.toLowerCase().includes(q)).map((c) => c.id),
  );

  return metrics.filter(
    (metric) =>
      metric.name.toLowerCase().includes(q) ||
      (metric.categoryId != null && matchingCategoryIds.has(metric.categoryId)),
  );
}
